import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import React, { useEffect, useRef } from "react";

const StoryModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const contentRef = useRef(null);

  // Scroll automático suave para accesibilidad
  useEffect(() => {
    if (open && contentRef.current) {
      const timer = setTimeout(() => {
        contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="historia-modal-titulo"
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          backgroundColor:
            theme.palette.mode === "dark" ? "#1c2a3d" : "#f8f5e9",
          backgroundImage:
            theme.palette.mode === "dark"
              ? "linear-gradient(rgba(28, 42, 61, 0.9), rgba(28, 42, 61, 0.9)), url(/assets/images/general/paper-texture-dark.jpg)"
              : "linear-gradient(rgba(248, 245, 233, 0.9), rgba(248, 245, 233, 0.9)), url(/assets/images/general/paper-texture.jpg)",
          backgroundSize: "cover",
          backgroundBlendMode: "overlay",
        },
      }}
    >
      <DialogTitle
        component={motion.div}
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        sx={{
          textAlign: "center",
          pb: 0,
          pt: 4,
          fontFamily: "'Quicksand', sans-serif",
        }}
      >
        <Typography
          variant="h3"
          component="h2"
          id="historia-modal-titulo"
          sx={{
            fontWeight: 700,
            color: theme.palette.mode === "dark" ? "#ffd54f" : "#8b5e34",
            textShadow:
              theme.palette.mode === "dark"
                ? "1px 1px 3px rgba(0,0,0,0.3)"
                : "1px 1px 2px rgba(139,94,52,0.2)",
          }}
        >
          Nuestra Historia
        </Typography>

        <Box
          sx={{
            width: "100px",
            height: "3px",
            background: theme.palette.mode === "dark" ? "#ffd54f" : "#8b5e34",
            borderRadius: "3px",
            mx: "auto",
            mt: 2,
            mb: 1,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 0 5px rgba(255,213,79,0.5)"
                : "0 0 5px rgba(139,94,52,0.3)",
          }}
        />

        <IconButton
          aria-label="cerrar"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: theme.palette.mode === "dark" ? "#ffd54f" : "#8b5e34",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        ref={contentRef}
        sx={{
          p: { xs: 2, sm: 4 },
          "&::-webkit-scrollbar": {
            width: "12px",
          },
          "&::-webkit-scrollbar-track": {
            background: theme.palette.mode === "dark" ? "#11192580" : "#f1ece0",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background:
              theme.palette.mode === "dark" ? "#ffd54f80" : "#8b5e3480",
            borderRadius: "10px",
            border: "3px solid transparent",
            backgroundClip: "content-box",
          },
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          sx={{ pb: 2 }}
        >
          <Box
            component="img"
            src="/assets/images/general/family-photo-frame.png"
            alt="Fotografía de Víctor con su familia"
            sx={{
              width: "100%",
              maxWidth: "400px",
              height: "auto",
              display: "block",
              margin: "0 auto 30px",
              borderRadius: "10px",
              boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
            }}
          />

          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontFamily: "'Quicksand', sans-serif",
              fontWeight: 700,
              mb: 3,
              color: theme.palette.mode === "dark" ? "#ffd54f" : "#8b5e34",
            }}
          >
            El comienzo inesperado de VicDown
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: "15px",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(0,0,0,0.2)"
                  : "rgba(255,255,255,0.5)",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(139,94,52,0.2)"
              }`,
              mb: 4,
            }}
          >
            <Typography
              paragraph
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.8,
                textIndent: "1.5em",
                mb: 2.5,
              }}
            >
              Todo comenzó hace 8 años, cuando nació nuestro hijo Víctor. Nadie
              nos había dicho durante el embarazo que vendría con síndrome de
              Down. Fue una sorpresa que cambió nuestras vidas para siempre.
            </Typography>

            <Typography
              paragraph
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.8,
                textIndent: "1.5em",
                mb: 2.5,
              }}
            >
              Los primeros meses fueron especialmente difíciles. Víctor estuvo
              muy malito e ingresado en el hospital. Pasamos noches enteras sin
              dormir, preocupados y asustados. Teníamos tantas preguntas y tan
              pocas respuestas... El sistema sanitario nos apoyaba con los
              aspectos médicos, pero nos sentíamos perdidos en todo lo demás.
            </Typography>

            <Typography
              paragraph
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.8,
                textIndent: "1.5em",
                mb: 2.5,
              }}
            >
              Lo más frustrante era buscar información sobre el síndrome de
              Down. Todo estaba disperso: un artículo aquí, un blog allá, un
              grupo de apoyo en otro lado... La información era fragmentada, a
              veces contradictoria, y resultaba agotador intentar unir todas las
              piezas mientras cuidábamos de nuestro pequeño.
            </Typography>
          </Paper>

          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontFamily: "'Quicksand', sans-serif",
              fontWeight: 700,
              mb: 3,
              color: theme.palette.mode === "dark" ? "#ffd54f" : "#8b5e34",
            }}
          >
            Víctor y sus hermanos: Un vínculo especial
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: "15px",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(0,0,0,0.2)"
                  : "rgba(255,255,255,0.5)",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(139,94,52,0.2)"
              }`,
              mb: 4,
            }}
          >
            <Typography
              paragraph
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.8,
                textIndent: "1.5em",
                mb: 2.5,
              }}
            >
              Una de las mayores bendiciones en la vida de Víctor son sus dos
              hermanos, Pedro y Alex. Desde el primer día, ellos lo acogieron
              con un amor incondicional que nos conmovió profundamente. Ver cómo
              lo protegen, cómo juegan con él adaptándose a su ritmo y cómo
              celebran cada uno de sus logros como si fuera una victoria
              familiar completa, es simplemente maravilloso.
            </Typography>

            <Typography
              paragraph
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.8,
                textIndent: "1.5em",
                mb: 2.5,
              }}
            >
              Víctor, con su personalidad única, es un poco "baguete", como
              decimos cariñosamente en casa. Le encanta tomarse las cosas con
              calma y a su propio ritmo. Pero no os dejéis engañar por esa
              aparente tranquilidad: ¡es tremendamente pillo cuando quiere! Sus
              travesuras siempre logran sacarnos una sonrisa, incluso en los
              días más difíciles.
            </Typography>

            <Typography
              paragraph
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.8,
                textIndent: "1.5em",
                mb: 2.5,
              }}
            >
              Entre los tres hermanos han creado un lenguaje propio, una
              complicidad especial. Es asombroso ver cómo Pedro y Alex han
              desarrollado una sensibilidad y empatía excepcionales gracias a su
              hermano. Ellos son, sin duda, sus más grandes admiradores y
              defensores.
            </Typography>
          </Paper>

          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontFamily: "'Quicksand', sans-serif",
              fontWeight: 700,
              mb: 3,
              color: theme.palette.mode === "dark" ? "#ffd54f" : "#8b5e34",
            }}
          >
            El amor que transformó el desafío en propósito
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: "15px",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(0,0,0,0.2)"
                  : "rgba(255,255,255,0.5)",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(139,94,52,0.2)"
              }`,
              mb: 4,
            }}
          >
            <Typography
              paragraph
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.8,
                textIndent: "1.5em",
                mb: 2.5,
              }}
            >
              A pesar de todos los desafíos, algo increíble sucedió: nos
              enamoramos profundamente de Víctor. Su sonrisa, su perseverancia y
              su forma única de ver el mundo nos enseñaron más sobre el amor
              incondicional de lo que jamás hubiéramos imaginado.
            </Typography>

            <Typography
              paragraph
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.8,
                textIndent: "1.5em",
                mb: 2.5,
              }}
            >
              Conforme Víctor fue creciendo, nuestra determinación también lo
              hizo. Pensaba constantemente: "Si a nosotros nos está costando
              tanto encontrar información y recursos, ¿cómo será para otras
              familias que quizás tengan menos acceso o tiempo para
              investigar?".
            </Typography>

            <Typography
              paragraph
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.8,
                textIndent: "1.5em",
                mb: 2.5,
                fontStyle: "italic",
              }}
            >
              "Si pudiéramos crear un espacio donde toda la información
              estuviera centralizada, donde las familias pudieran conectarse,
              donde los especialistas pudieran compartir sus conocimientos...
              ¡cuántas vidas podríamos ayudar!".
            </Typography>
          </Paper>

          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontFamily: "'Quicksand', sans-serif",
              fontWeight: 700,
              mb: 3,
              color: theme.palette.mode === "dark" ? "#ffd54f" : "#8b5e34",
            }}
          >
            El nacimiento de VicDown
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: "15px",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(0,0,0,0.2)"
                  : "rgba(255,255,255,0.5)",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(139,94,52,0.2)"
              }`,
              mb: 3,
            }}
          >
            <Typography
              paragraph
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.8,
                textIndent: "1.5em",
                mb: 2.5,
              }}
            >
              Y así nació VicDown, en honor a nuestro Víctor. No solo como un
              proyecto tecnológico, sino como una misión personal: crear una
              comunidad donde las familias como la nuestra pudieran encontrar
              todo lo que necesitan en un solo lugar.
            </Typography>

            <Typography
              paragraph
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.8,
                textIndent: "1.5em",
                mb: 2.5,
              }}
            >
              Queríamos un espacio donde el conocimiento estuviera unificado,
              donde los padres pudieran compartir sus experiencias, donde los
              especialistas pudieran ofrecer su ayuda y donde las personas que
              viven con síndrome de Down tuvieran voz propia.
            </Typography>

            <Typography
              paragraph
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.8,
                textIndent: "1.5em",
                mb: 2.5,
              }}
            >
              Hoy, VicDown es mucho más que una plataforma. Es el legado de
              Víctor, es un abrazo virtual para todas las familias que han
              sentido lo que nosotros sentimos y es una promesa de que, con la
              información correcta y el apoyo adecuado, nuestros hijos que viven
              con síndrome de Down pueden alcanzar su máximo potencial y vivir
              vidas plenas y felices.
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "1.1rem", sm: "1.2rem" },
                fontWeight: 600,
                textAlign: "center",
                mt: 4,
                fontStyle: "italic",
                color: theme.palette.mode === "dark" ? "#ffd54f" : "#8b5e34",
              }}
            >
              Porque cada sonrisa de Víctor, cada complicidad con sus hermanos
              Pedro y Alex, nos recuerda que este camino, aunque diferente, está
              lleno de momentos maravillosos que merecen ser compartidos.
            </Typography>
          </Paper>

          <Box
            component={motion.div}
            whileHover={{ scale: 1.03 }}
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontFamily: "'Dancing Script', cursive",
                fontSize: "1.4rem",
                textAlign: "right",
                fontWeight: 600,
                color: theme.palette.mode === "dark" ? "#ffd54f" : "#8b5e34",
              }}
            >
              Con amor, el equipo de VicDown
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default StoryModal;
